import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import Skeleton from "@mui/material/Skeleton";
import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import useIntegration from "@app/analytics/hooks/integrations/useIntegration";
import * as api from "@app/common/api";
import { DATA_INGEST_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import { ELLIPSIS_SX } from "@app/common/styles";
import { getDisplayUrl } from "@app/common/utils";
import Config from "@app/config";

import Alert from "@rui/Alert";
import Button from "@rui/Button";
import CopyButton from "@rui/CopyButton";
import Link from "@rui/Link";
import Modal, { ModalProps } from "@rui/Modal";
import ModalHeader from "@rui/Modal/ModalHeader";
import { Column, Row, StackProps } from "@rui/Stack";

const LinkContainer = styled((props: StackProps) => (
  <Row variant="apart" spacing="4px" {...props} />
))(({ theme }) => ({
  marginTop: "4px",
  borderRadius: "8px",
  border: `1px solid ${theme.palette.shades.neutral[100]}`,
  padding: theme.spacing(0.5),
  paddingLeft: theme.spacing(2),
  width: "100%",
}));

const getMagicLink = async (): Promise<string> => {
  const json = await api.get(Config.apps.teams.ADMIN_MAGIC_LINK, {
    withCreds: true,
  });
  return json.url;
};

const StyledList = styled("ol")(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: "1rem",
  color: theme.palette.shades.neutral[600],
  margin: 0,
  padding: "0 0 0 20px",
  "& li": {
    padding: theme.spacing(0.5, 0),
    "&:last-of-type": { paddingBottom: 0 },
    margin: 0,
    lineHeight: "20px",
  },
}));

const providerConfig =
  DATA_INGEST_PROVIDERS_BY_ID.microsoft_graph_teams_chat_ingest;

const ConnectTeamsChannelsModal = ({
  open,
  onClose,
}: {
  open: ModalProps["open"];
  onClose: ModalProps["onClose"];
}) => {
  const {
    data: magicLink,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["magicLink"],
    queryFn: getMagicLink,
  });

  const { authorize } = useIntegration();
  const location = useLocation();

  const handleClickConnect = () => {
    authorize.mutate({
      providerId: providerConfig.id,
      returnRoute: location.pathname,
      scope: "user",
      connectParams: {
        include_admin_scopes: "true",
      },
    });
  };

  return (
    <Modal
      open={open}
      sx={{ width: "100%", minWidth: "300px", maxWidth: "500px" }}
      onClose={onClose}
    >
      <Column variant="left" spacing={2}>
        <ModalHeader title="Connect Teams Channels" onClose={onClose} />
        <Column variant="left" spacing={1}>
          <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
            Enabling Teams channels with Read requires additional permission
            from your Teams admin.
          </Typography>
          <StyledList>
            <li>Copy the magic link below</li>
            <li>
              Send the link to your Teams admin so they can approve the
              permissions
            </li>
          </StyledList>
        </Column>
        {isLoading ? (
          <Skeleton
            sx={{ borderRadius: "8px" }}
            width="100%"
            height="40px"
            variant="rectangular"
          />
        ) : isError ? (
          <Alert embedded severity="error">
            There was an error loading the link. Please refresh the page or
            contact support@read.ai
          </Alert>
        ) : (
          <LinkContainer>
            <Typography
              variant="bodyPrimaryReg"
              color="shades.neutral.500"
              sx={ELLIPSIS_SX}
            >
              {getDisplayUrl(magicLink ?? "")}
            </Typography>
            <CopyButton
              copyValue={magicLink ?? ""}
              renderButton={(props, copied) => (
                <Button sx={{ height: "40px", width: "80px" }} {...props}>
                  {copied ? "Copied!" : "Copy"}
                </Button>
              )}
            />
          </LinkContainer>
        )}

        <Typography
          variant="bodyPrimaryReg"
          sx={{
            backgroundColor: "shades.primary.50",
            padding: "8px",
            borderRadius: "8px",
            color: "shades.neutral.600",
          }}
        >
          If your admin has already approved permissions,{" "}
          <Link onClick={handleClickConnect}>
            click here to continue with setup
          </Link>
          .
        </Typography>
      </Column>
    </Modal>
  );
};

export default ConnectTeamsChannelsModal;
